import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Heart, Eye, Crown, Share2, ChevronLeft, BookOpen, Sparkles, User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getStoredLang } from "@/utils/i18n";
import ambientMusic from "@/utils/AmbientMusic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hall of Stories — saga detail. Public route (works without login).
 * Shows the cover image hero, character info, full bosses gallery and
 * (for logged-in users) a like button.
 */
export default function HallDetail() {
  const { pid } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const lang = getStoredLang();
  const isIt = lang === "it";

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/hall/${pid}`, { headers });
        setStory(res.data);
      } catch {
        setStory(null);
      } finally {
        setLoading(false);
      }
    };
    if (pid) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // iter 74 — keep the Hall mood while reading a saga detail page
  useEffect(() => { ambientMusic.play('hall'); }, []);

  const toggleLike = async () => {
    if (!token) { navigate("/auth"); return; }
    if (!story) return;
    setBusy(true);
    try {
      const next = !story.liked_by_me;
      if (next) {
        const res = await axios.post(`${API}/hall/${pid}/like`, {}, { headers });
        setStory({ ...story, liked_by_me: true, likes_count: res.data.likes_count });
      } else {
        const res = await axios.delete(`${API}/hall/${pid}/like`, { headers });
        setStory({ ...story, liked_by_me: false, likes_count: res.data.likes_count });
      }
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: story.title, text: story.description, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(isIt ? "Link copiato!" : "Link copied!");
      }
    } catch {
      // user cancelled or no support
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex flex-col items-center justify-center text-center px-4">
        <BookOpen size={48} className="text-[#cfb53b]/30 mb-4" />
        <h2 className="font-heading text-2xl text-[#cfb53b] mb-2">
          {isIt ? "Saga non trovata" : "Saga not found"}
        </h2>
        <p className="text-[#94a3b8] mb-6">
          {isIt ? "La pubblicazione potrebbe essere stata rimossa." : "The publication may have been removed."}
        </p>
        <Button asChild className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading">
          <Link to="/hall">{isIt ? "Torna alla Hall" : "Back to Hall"}</Link>
        </Button>
      </div>
    );
  }

  const cover = story.cover_image_url;
  const char = story.character || {};
  const bosses = story.bosses || [];
  const highlights = story.highlight_pages || [];

  const HIGHLIGHT_LABELS = isIt
    ? { opening: "Apertura", boss: "Boss", chapter: "Fine capitolo" }
    : { opening: "Opening", boss: "Boss", chapter: "Chapter end" };

  return (
    <div className="min-h-screen bg-[#0a0a0c] relative" data-testid="hall-detail-page">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c] via-[#1a1a1d] to-[#0a0a0c] pointer-events-none" />

      {/* Hero cover */}
      <div className="relative h-[40vh] sm:h-[55vh] w-full overflow-hidden">
        {cover ? (
          <img src={cover} alt={story.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#1a1a1d] flex items-center justify-center text-[#cfb53b]/20">
            <BookOpen size={80} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/40 to-transparent" />

        {/* Top nav overlay */}
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-black/40 backdrop-blur-sm"
            data-testid="hall-detail-back"
          >
            <ChevronLeft size={20} />
            <span className="hidden sm:inline ml-1">Hall</span>
          </Button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={share}
              className="px-3 py-2 rounded-md bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white flex items-center gap-1 text-sm"
              data-testid="hall-detail-share"
            >
              <Share2 size={14} />
              <span className="hidden sm:inline">{isIt ? "Condividi" : "Share"}</span>
            </button>
            <button
              type="button"
              onClick={toggleLike}
              disabled={busy}
              className={`px-3 py-2 rounded-md backdrop-blur-sm flex items-center gap-1 text-sm font-heading transition-all ${
                story.liked_by_me
                  ? "bg-pink-600 hover:bg-pink-500 text-white"
                  : "bg-black/40 hover:bg-pink-700/80 text-white"
              }`}
              data-testid="hall-detail-like"
            >
              <Heart size={14} fill={story.liked_by_me ? "currentColor" : "none"} />
              <span>{story.likes_count}</span>
            </button>
          </div>
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 max-w-4xl mx-auto">
          <h1 className="font-heading text-3xl sm:text-5xl font-bold text-[#cfb53b] drop-shadow-lg leading-tight">
            {story.title}
          </h1>
          <div className="mt-2 flex items-center flex-wrap gap-2 sm:gap-4 text-sm sm:text-base text-[#e2e8f0]">
            <span className="flex items-center gap-1">
              <User size={14} className="text-[#cfb53b]" />
              <span className="font-heading">{char.name}</span>
              <span className="text-[#94a3b8]">— {isIt ? "L." : "Lv."} {char.level}</span>
              <span className="text-[#94a3b8] capitalize">{char.class_type}</span>
            </span>
            <span className="text-[#cfb53b]/50">·</span>
            <span className="text-[#94a3b8]">
              {isIt ? "di" : "by"} <span className="text-[#cfb53b]">{story.author_username}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <StatBlock icon={<Sparkles size={18} />} label={isIt ? "Capitoli" : "Chapters"} value={story.chapters} />
          <StatBlock icon={<Crown size={18} />} label={isIt ? "Boss" : "Bosses"} value={story.bosses_defeated} accent="rgb(139,0,0)" />
          <StatBlock icon={<BookOpen size={18} />} label={isIt ? "Pagine" : "Pages"} value={story.total_pages} />
          <StatBlock icon={<Eye size={18} />} label={isIt ? "Visite" : "Views"} value={story.views} />
        </div>

        {/* Description */}
        {story.description && (
          <div className="border-l-4 border-[#cfb53b]/40 bg-[#1a1a1d]/60 p-4 sm:p-5 rounded-r-md">
            <p className="text-[#e2e8f0] italic font-body leading-relaxed">{story.description}</p>
          </div>
        )}

        {/* Bosses Gallery */}
        {bosses.length > 0 && (
          <div data-testid="hall-detail-bosses">
            <h2 className="font-heading text-xl sm:text-2xl text-[#cfb53b] mb-3 flex items-center gap-2">
              <Crown size={20} className="text-[#8b0000]" />
              {isIt ? "Boss Sconfitti" : "Defeated Bosses"}
            </h2>
            <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
              {bosses.map((b, i) => (
                <div
                  key={`${b.name}-${i}`}
                  className="bg-[#1a1a1d]/80 border border-[#8b0000]/30 rounded-md overflow-hidden hover:border-[#8b0000]/60 transition-all"
                  data-testid={`hall-boss-${i}`}
                >
                  <div className="relative aspect-square bg-[#0a0a0c]">
                    {b.image_url ? (
                      <img src={b.image_url} alt={b.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#8b0000]/30">
                        <Crown size={36} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] to-transparent" />
                    {b.chapter && (
                      <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-heading text-[#cfb53b]">
                        {isIt ? "Cap." : "Ch."} {b.chapter}
                      </div>
                    )}
                  </div>
                  <div className="p-2 space-y-0.5">
                    <h3 className="font-heading text-sm text-[#e2e8f0] truncate">{b.name}</h3>
                    {b.description && (
                      <p className="text-[10px] text-[#94a3b8] line-clamp-2">{b.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Iter 96 — Leggi la saga: narrative highlights (opening + boss + chapter-end) */}
        {highlights.length > 0 && (
          <div data-testid="hall-detail-highlights">
            <h2 className="font-heading text-xl sm:text-2xl text-[#cfb53b] mb-3 flex items-center gap-2">
              <BookOpen size={20} />
              {isIt ? "Leggi la saga" : "Read the saga"}
              <span className="ml-1 text-[10px] sm:text-xs font-body text-[#94a3b8] uppercase tracking-wider">
                {isIt ? "Estratti narrativi" : "Narrative highlights"}
              </span>
            </h2>
            <div className="space-y-6 sm:space-y-8">
              {highlights.map((h, i) => (
                <article
                  key={`hl-${h.page_number}-${i}`}
                  className="bg-[#1a1a1d]/60 border border-[#cfb53b]/15 rounded-md overflow-hidden hover:border-[#cfb53b]/35 transition-colors"
                  data-testid={`hall-highlight-${i}`}
                >
                  {h.image_url && (
                    <div className="relative w-full aspect-[16/9] bg-[#0a0a0c] overflow-hidden">
                      <img
                        src={h.image_url}
                        alt={`page ${h.page_number}`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c]/80 via-transparent to-transparent" />
                      <div className="absolute top-2 left-2 flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-heading uppercase tracking-wider rounded ${
                          h.type === "boss"
                            ? "bg-[#8b0000]/80 text-white"
                            : h.type === "opening"
                            ? "bg-[#cfb53b]/80 text-[#0a0a0c]"
                            : "bg-[#475569]/80 text-white"
                        }`}>
                          {HIGHLIGHT_LABELS[h.type] || h.type}
                        </span>
                        {h.chapter && (
                          <span className="px-2 py-0.5 text-[10px] font-heading rounded bg-black/60 text-[#cfb53b]">
                            {isIt ? "Cap." : "Ch."} {h.chapter}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="p-4 sm:p-6">
                    {!h.image_url && (
                      <div className="mb-2 flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-heading uppercase tracking-wider rounded ${
                          h.type === "boss" ? "bg-[#8b0000]/80 text-white" :
                          h.type === "opening" ? "bg-[#cfb53b]/80 text-[#0a0a0c]" :
                          "bg-[#475569]/80 text-white"
                        }`}>
                          {HIGHLIGHT_LABELS[h.type] || h.type}
                        </span>
                        {h.chapter && (
                          <span className="text-[10px] font-heading text-[#cfb53b]">
                            {isIt ? "Cap." : "Ch."} {h.chapter}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="font-body text-[#e2e8f0] leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                      {h.text}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* CTA for guests */}
        {!user && (
          <div className="border border-[#cfb53b]/30 bg-[#cfb53b]/5 rounded-md p-4 sm:p-6 text-center">
            <p className="text-[#e2e8f0] font-body mb-3">
              {isIt
                ? "Crea il tuo personaggio, vivi una saga generata dall'IA e pubblicala anche tu."
                : "Create your character, live an AI-generated saga, and publish your own."}
            </p>
            <Button onClick={() => navigate("/auth")} className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading">
              {isIt ? "Inizia la tua leggenda" : "Begin your legend"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBlock({ icon, label, value, accent = "rgb(207,181,59)" }) {
  return (
    <div className="bg-[#1a1a1d]/80 border border-[#cfb53b]/15 rounded-md p-3 flex items-center gap-3">
      <div className="shrink-0" style={{ color: accent }}>{icon}</div>
      <div className="min-w-0">
        <div className="font-heading text-xl sm:text-2xl text-[#e2e8f0] leading-none">{value}</div>
        <div className="text-[10px] sm:text-xs text-[#94a3b8] uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}
